import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Flower } from 'iconoir-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password, remember);
      const next = searchParams.get('next');
      // Only honor relative same-origin paths — never external redirects.
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      if (data?.is_paid && safeNext) navigate(safeNext);
      else navigate(data?.is_paid ? '/dashboard' : '/paywall');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative"
      data-testid="login-page"
      style={{ background: '#FFFFFF' }}
    >
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1735052712464-9d24b69be5f5?w=1600&q=80&auto=format&fit=crop"
          alt="South Asian bride and groom walking together"
          className="w-full h-full object-cover"
        />
        {/* Warm terracotta-to-moss overlay tying the image into the brand palette */}
        <div className="absolute inset-0 bg-gradient-to-br from-terracotta/30 via-transparent to-moss/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-transparent" />
        {/* Floating ornament — wedding-card style */}
        <div className="absolute bottom-12 left-12 right-12 text-page">
          <div className="flex items-center gap-3 mb-3 opacity-90">
            <div className="h-px w-12 bg-ochre/70" />
            <span className="text-ochre text-lg font-serif">✦</span>
            <div className="h-px w-12 bg-ochre/70" />
          </div>
          <p className="font-serif italic text-2xl xl:text-3xl font-light leading-snug max-w-md drop-shadow-md">
            "A love letter to the chaos of big, beautiful South Asian weddings."
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 relative">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <Flower className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
            <span className="font-brand text-2xl text-charcoal">Shaadi Planner</span>
          </div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight mb-4">
            Welcome back
          </h1>
          <p className="text-muted font-light mb-10">
            Sign in to continue planning your perfect celebration.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-sm text-terracotta text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-charcoal font-sans text-sm uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field h-12"
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-charcoal font-sans text-sm uppercase tracking-wider">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field h-12"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none group" data-testid="login-remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 accent-terracotta cursor-pointer"
                data-testid="login-remember-checkbox"
              />
              <span className="text-sm text-charcoal">
                Remember this device
                <span className="text-muted font-mono text-xs ml-2 group-hover:text-charcoal/70 transition-colors">30 days</span>
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-muted hover:text-terracotta transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="mt-8 text-center text-muted">
            Don't have an account?{' '}
            <Link to="/register" className="text-terracotta hover:text-terracotta-dark transition-colors" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
